// Every lazy page carries `.preload()` so App.tsx can warm the boot route's
// chunk while the gate chain runs (see ./preload.ts). Same React.lazy otherwise.
import { lazyWithPreload as lazy } from "./preload";
import {
  ReceiptText,
  LayoutDashboard,
  CalendarClock,
  ListTodo,
  Mail,
  MessageSquarePlus,
  MessageCircle,
  CalendarDays,
  Camera,
  AlertCircle,
  Users,
  GraduationCap,
  UserPlus,
  ClipboardList,
  Gamepad2,
  Gift,
  Baby,
  UserCheck,
  LayoutGrid,
  Sparkles,
  Kanban,
  RefreshCw,
  Clock,
  Thermometer,
  BarChart3,
  TrendingUp,
  Star,
  ShieldCheck,
  Bug,
  Settings as SettingsIcon,
  Brain,
  Bell,
  Waypoints,
  FileText,
  School,
  Umbrella,
  Radar,
  User,
  FileClock,
  // Car for /planning/reiskosten (reiskosten km payroll month).
  Car,
  Signal,
  // Building2 for /childcare/floorplan (Play 3 Living Floorplan).
  Building2,
  CalendarRange,
  // Smartphone for /parent-adoption (B1 parent-app adoption panel).
  Smartphone,
  // ReceiptEuro for /declaraties (Declaraties Phase 1 Task 3) — teacher
  // expense claims, staffNav-only like /me and /my-schedule.
  ReceiptEuro,
  Inbox,
  // Gift for /my-wrapped (TK Wrapped, Play 2).
  // 🎄 Gift import hidden along with the route below — see note there.
  // Gift,
} from "lucide-react";
import HiringPage from "./HiringPage";
import { TEDDYVERSE_ENABLED } from "@/lib/teddyverseFeature";
import type { RouteDef } from "./types";
import { PARENT_MESSAGE_TOPICS_ENABLED } from "@/lib/parentMessageTopicsFeature";

// =====================================================
// LAZY-LOADED PAGES
// Mirrors src/App.tsx's lazy() declarations exactly (same target modules,
// same default/named export handling) — only the import specifiers are
// rewritten from App.tsx's `./`-relative paths to the `@/` alias, since this
// file lives at src/routes/ rather than src/.
// =====================================================

const StaffingHorizonPage = lazy(() => import("@/modules/planning/pages/StaffingHorizonPage"));
const StaffPage = lazy(() => import("@/pages/Staff"));
const StaffFill = lazy(() => import("@/pages/StaffFill"));
const Auth = lazy(() => import("@/pages/Auth"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const StaffProfile = lazy(() => import("@/pages/StaffProfile"));
const Interns = lazy(() => import("@/pages/Interns"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const Reports = lazy(() => import("@/pages/Reports"));
const ActivityFeed = lazy(() => import("@/pages/ActivityFeed"));
const Games = lazy(() => import("@/pages/Games"));
const Lootjes = lazy(() => import("@/pages/Lootjes"));
const Sinterklaas = lazy(() => import("@/pages/Sinterklaas"));
const LootjesGroup = lazy(() => import("@/pages/LootjesGroup"));
const GamesBattle = lazy(() => import("@/pages/games/GamesBattle"));
const GamesConsole = lazy(() => import("@/pages/games/GamesConsole"));
const QuestSubmitPage = lazy(() => import("@/pages/games/QuestSubmitPage"));
const GamesLaunch = lazy(() => import("@/pages/games/GamesLaunch"));
const LeadershipKPI = lazy(() => import("@/pages/LeadershipKPI"));
const Email = lazy(() => import("@/pages/Email"));
const ManagerParentQuestions = lazy(() => import("@/pages/ManagerParentQuestions"));
const GmailCallback = lazy(() => import("@/pages/GmailCallback"));
const SettingsPage = lazy(() => import("@/pages/Settings"));
const EmployesSync = lazy(() => import("@/pages/EmployesSync"));
// Finance Command Center hub (docs/plans/2026-08-24-finance-command-center.md
// Task 1): the 10 absorbed finance page routes (/billing/invoices,
// /billing/open-invoices, /billing/payments, /billing/runs,
// /billing/tarieven, /payables, /payables/submit, /payables/personal,
// /payables/review, /payables/bank) are redirects below — their page
// components now lazy-load from the hub's tab registry
// (src/pages/finance/financeTabs.ts), not from this file.
const FinanceHubPage = lazy(() => import("@/pages/finance/FinanceHubPage"));
const BillingFamilyAccount = lazy(() => import("@/pages/BillingFamilyAccount"));
const BillingBooks = lazy(() => import("@/pages/BillingBooks"));
const BillingInvoiceDetail = lazy(() => import("@/pages/BillingInvoiceDetail"));
const ComplianceDashboard = lazy(() => import("@/pages/ComplianceDashboard"));
const DocumentTypesAdmin = lazy(() => import("@/pages/DocumentTypesAdmin"));
const BugReportsCenter = lazy(() => import("@/pages/BugReportsCenter"));
const LaunchCockpit = lazy(() => import("@/pages/launch/LaunchCockpit"));
const OnboardingAdmin = lazy(() => import("@/pages/OnboardingAdmin"));
const JourneyHouseholdTest = lazy(() => import("@/pages/JourneyHouseholdTest"));
const JourneyOps = lazy(() => import("@/pages/JourneyOps"));
const Planning = lazy(() => import("@/pages/Planning"));
const EmploymentJourney = lazy(() => import("@/pages/EmploymentJourney"));
const Hub = lazy(() => import("@/pages/Hub"));
const WorkPlanning = lazy(() => import("@/pages/WorkPlanning"));
const ParentRequests = lazy(() => import("@/pages/ParentRequests"));
const NoShowQueue = lazy(() => import("@/pages/NoShowQueue"));
const DriftSignalsPage = lazy(() => import("@/pages/DriftSignalsPage"));
const ParentMessages = lazy(() => import("@/pages/ParentMessages"));
const ParentMessageTopics = lazy(() => import("@/pages/ParentMessageTopics"));
const MessageCenter = lazy(() => import("@/pages/MessageCenter"));
const ParentContent = lazy(() => import("@/pages/ParentContent"));
const MyProfileRedirect = lazy(() => import("@/pages/MyProfileRedirect"));
// Declaraties Phase 1 Task 3 (docs/plans/2026-08-26-declaraties.md) — teacher
// expense claims: own list + nieuw modal, one route, no manager-rail entry.
const DeclaratiesPage = lazy(() => import("@/pages/DeclaratiesPage"));
// Assistant Manager Protocol 2026 — the shareable read-and-sign link for the
// core team, with the admin "who has signed" view. Text is server-side
// (staff_agreement_documents RLS); the page explains, the DB enforces.
const ManagerProtocol = lazy(() => import("@/pages/ManagerProtocol"));
// Role branch for "/" (staff → /my-schedule, else Dashboard) — kept lazy like
// every other registry element so importing the registry itself (e.g. from
// invariants.test.ts) never eagerly pulls in useCurrentUserStaff → the
// supabase client.
const RootRoute = lazy(() => import("./RootRoute"));
const GrowHome = lazy(() => import("./GrowHome"));
// Teddy Academy (docs/plans/2026-10-05-teddy-academy.md).
const AcademyHome = lazy(() => import("@/modules/academy/pages/AcademyHome"));
const VoiceHome = lazy(() => import("@/modules/academy/pages/VoiceHome"));
const ActivitiesPage = lazy(() => import("@/modules/play/pages/ActivitiesPage"));

// Cockpit (Reports 2)
const CockpitOverview = lazy(() => import("@/pages/cockpit/CockpitOverview"));
const CockpitFinancials = lazy(() => import("@/pages/cockpit/CockpitFinancials"));
const LeaversPage = lazy(() => import("@/pages/children/LeaversPage"));
const PhotoFlagsPage = lazy(() => import("@/pages/children/PhotoFlagsPage"));
const ParentAdoptionPanel = lazy(() => import("@/pages/ParentAdoptionPanel"));

// Widgets
const StandaloneDiscWidget = lazy(() => import("@/pages/widget/DiscAssessmentWidget"));
const ContractViewPage = lazy(() => import("@/pages/contract/ContractViewPage"));
const ApplyPage = lazy(() => import("@/pages/Apply"));
const StaffDiscAssessment = lazy(() => import("@/pages/StaffDiscAssessment"));
const TourManagePage = lazy(() => import("@/pages/TourManagePage"));

// Grow Buddy
const OnboardingPage = lazy(() => import("@/modules/growbuddy/pages/OnboardingPage"));
const KnowledgePage = lazy(() =>
  import("@/modules/growbuddy/pages/KnowledgePage").then((m) => ({ default: m.KnowledgePage })),
);
const Coaching = lazy(() => import("@/pages/Coaching"));
const DocumentReaderPage = lazy(() => import("@/modules/growbuddy/pages/DocumentReaderPage"));

// Shared fix-todo checklist (docs/plans/2026-08-09-fix-todo-page.md)
const FixTodoPage = lazy(() => import("@/pages/todo/FixTodoPage"));

// GGD pack — compliance board (docs/plans/2026-09-07-ggd-pack.md Task 1)
const GgdPackPage = lazy(() => import("@/modules/ggd/pages/GgdPackPage"));
const GgdPackPrintPage = lazy(() => import("@/modules/ggd/pages/GgdPackPrintPage"));

// Issues Portal
const VoorJouPage = lazy(() => import("@/modules/inbox/pages/VoorJouPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));

const IssuesPage = lazy(() => import("@/pages/issues"));
const IssuesSubmit = lazy(() => import("@/pages/issues/IssuesSubmit"));
const IssuesDashboard = lazy(() => import("@/pages/issues/IssuesDashboard"));
const IssuesWidget = lazy(() => import("@/pages/issues/IssuesWidget"));
const IssueStatus = lazy(() => import("@/pages/issues/IssueStatus"));

// Kiosk
const KioskLanding = lazy(() => import("@/pages/KioskLanding"));
const Kiosk = lazy(() => import("@/pages/Kiosk"));
const KioskWeekGrid = lazy(() => import("@/pages/labs/KioskWeekGrid"));
const KioskArena = lazy(() => import("@/pages/kiosk/KioskArena"));

// Child Registration
const RegisterChild = lazy(() => import("@/pages/RegisterChild"));
const RegisterChildSuccess = lazy(() => import("@/pages/RegisterChildSuccess"));
const DocViewer = lazy(() => import("@/pages/DocViewer"));
const DocsIndex = lazy(() => import("@/pages/DocsIndex"));
const StaffDocsIndex = lazy(() => import("@/pages/StaffDocsIndex"));
const Events = lazy(() => import("@/pages/Events"));
const SnapsPage = lazy(() => import("@/pages/SnapsPage"));
const SnapMatchPage = lazy(() => import("@/pages/snaps/SnapMatchPage"));
const Tours = lazy(() => import("@/pages/Tours"));
const RegisterWidget = lazy(() => import("@/pages/widgets/RegisterWidget"));
const ChildrenPage = lazy(() => import("@/modules/children/pages/ChildrenPage"));
const ChildProfilePage = lazy(() => import("@/modules/children/pages/ChildProfilePage"));
const ControlRoomPage = lazy(() => import("@/modules/planning/pages/ControlRoomPage"));
// OverplanningRadarPage wrapper removed (nav-ia rev2): /childcare/overplanning
// is now a redirect to /childcare/overzicht?tab=herverdelen — the hub renders
// OverplanningView directly. Same fate as the week-overview/bezettingsradar
// wrappers when /childcare/overzicht absorbed them (#858).
const WeekPlanEditorPage = lazy(() => import("@/modules/planning/pages/WeekPlanEditorPage"));
const PlanningHubPage = lazy(() => import("@/modules/planning/pages/PlanningHubPage"));
const LeaveSyncPage = lazy(() => import("@/modules/planning/pages/LeaveSyncPage"));
const SubstituteLoopPage = lazy(() => import("@/modules/planning/pages/SubstituteLoopPage"));
const TeacherHoursPage = lazy(() => import("@/modules/planning/pages/TeacherHoursPage"));
const SignalsPage = lazy(() => import("@/modules/planning/pages/SignalsPage"));
const PatternsPage = lazy(() => import("@/modules/planning/pages/PatternsPage"));
const HoursRequestsPage = lazy(() => import("@/modules/planning/pages/HoursRequestsPage"));
const ReiskostenMonthPage = lazy(() => import("@/modules/planning/pages/ReiskostenMonthPage"));
const GalaRsvpOverviewPage = lazy(() => import("@/modules/planning/pages/GalaRsvpOverviewPage"));
const PortabaseChangesPage = lazy(() => import("@/modules/planning/pages/PortabaseChangesPage"));
const FloorplanPage = lazy(() => import("@/modules/planning/pages/FloorplanPage"));
const OperationsGridPage = lazy(() => import("@/modules/planning/pages/OperationsGridPage"));
const SchoolsCalendarPage = lazy(() => import("@/modules/planning/pages/SchoolsCalendarPage"));
const VerlofcentrumPage = lazy(() => import("@/modules/planning/pages/VerlofcentrumPage"));
const StaffVacationsPage = lazy(() => import("@/modules/planning/pages/StaffVacationsPage"));
const SicknessOverviewPage = lazy(() => import("@/modules/planning/pages/SicknessOverviewPage"));
const RadarHubPage = lazy(() => import("@/modules/radar/pages/RadarHubPage"));
const AutoPlanPage = lazy(() => import("@/pages/childcare/AutoPlanPage"));
const MySchedule = lazy(() => import("@/pages/MySchedule"));
const Conversations = lazy(() => import("@/pages/Conversations"));
const VoicePractice = lazy(() => import("@/modules/academy/pages/VoicePractice"));
// 🎄 hidden until Xmas — see /my-wrapped block below
// const WrappedPage = lazy(() => import("@/modules/planning/pages/WrappedPage"));

const MOCK_ROUTES: RouteDef[] = __MOCK_ROUTES_ENABLED__ ? [
  {
    kind: "page",
    path: "/mock/teddyverse",
    element: lazy(() => import("@/modules/teddyverse/components/TeddyversePreview")),
    layout: "none",
    gate: "public",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/mock/bestellen",
    element: lazy(() => import("@/pages/mock/BestellenPage")),
    layout: "none",
    gate: "public",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/mock/design-studio",
    element: lazy(() => import("@/pages/mock/DesignStudioPage")),
    layout: "none",
    gate: "public",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/mock/akkoord",
    element: lazy(() => import("@/pages/mock/AkkoordPage")),
    layout: "none",
    gate: "public",
    discovery: { reason: "drill-down" },
  },
] : [];

// =====================================================
// REGISTRY
// Every <Route> from src/App.tsx (origin/main@2836843, rebased from the
// original 12d60ac census to pick up /parent-requests, landed by #525 while
// this branch was in flight), one RouteDef each.
// Excluded by design (see plan/task): the unauthenticated fallback redirects
// (/, /staff/*, /issues/*, /contracts/*, /labs/*, /grow/* → /auth) are
// auth-shell behavior, not discoverable surfaces — they exist only because
// `isAuthenticated` is false, not as independent routes.
//
// Registry order is NOT nav order: since nav-ia rev2 (docs/plans/nav-ia/)
// the manager sidebar is a flat rail of 13 destinations with tab entries,
// ordered by src/routes/discoveryRail.ts (RAIL_SPEC) — this file only
// carries each nav entry's metadata (title/icon/badge/exact/tabOf). Entries
// below stay grouped by their pre-rail sections purely for readability.
// Non-nav entries (redirects, drill-downs, rail pills) carry no ordering
// contract; they sit wherever's most readable, usually right after the
// route they satellite.
// =====================================================

export const routeRegistry: RouteDef[] = [
  // ---------------------------------------------------
  // PUBLIC — no auth required
  // ---------------------------------------------------
  {
    kind: "page",
    path: "/auth",
    element: Auth,
    layout: "none",
    gate: "public",
    discovery: { reason: "auth-flow" },
  },
  {
    kind: "page",
    path: "/gmail-callback",
    element: GmailCallback,
    layout: "none",
    gate: "public",
    discovery: { reason: "auth-flow" },
  },
  {
    kind: "page",
    path: "/widget/disc-assessment",
    element: StandaloneDiscWidget,
    layout: "none",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/widget/issues",
    element: IssuesWidget,
    layout: "none",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/widget/register",
    element: RegisterWidget,
    layout: "none",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  // /logit is a bare-URL alias for the same IssuesWidget (short link for QR
  // codes / posters), same as /widget/issues.
  {
    kind: "page",
    path: "/logit",
    element: IssuesWidget,
    layout: "none",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/apply",
    element: ApplyPage,
    layout: "none",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/contract/view/:token",
    element: ContractViewPage,
    layout: "none",
    gate: "public",
    discovery: { reason: "token-link" },
  },
  {
    kind: "page",
    path: "/issue-status/:token",
    element: IssueStatus,
    layout: "none",
    gate: "public",
    discovery: { reason: "token-link" },
  },
  {
    kind: "page",
    path: "/register-child",
    element: RegisterChild,
    layout: "none",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/register-child/success",
    element: RegisterChildSuccess,
    layout: "none",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/staff-disc/:token",
    element: StaffDiscAssessment,
    layout: "none",
    gate: "public",
    discovery: { reason: "token-link" },
  },
  {
    kind: "page",
    path: "/tour/manage/:token",
    element: TourManagePage,
    layout: "none",
    gate: "public",
    discovery: { reason: "token-link" },
  },
  // /docs (bare index) has no :token param and no personalization — an
  // external entry point (public site link), not a token-gated view.
  {
    kind: "page",
    path: "/docs",
    element: DocsIndex,
    layout: "public",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  // /docs/staff must be declared before /docs/:doc. React Router v6 uses
  // score-based ranking (static segments beat dynamic ones) so declaration
  // order doesn't strictly matter here, but placing it first is explicit
  // and consistent with the registry's intent.
  {
    kind: "page",
    path: "/docs/staff",
    element: StaffDocsIndex,
    layout: "public",
    gate: "public",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/docs/:doc",
    element: DocViewer,
    layout: "public",
    gate: "public",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/docs/:doc/:section",
    element: DocViewer,
    layout: "public",
    gate: "public",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/kiosk",
    element: KioskLanding,
    layout: "none",
    gate: "public",
    discovery: { reason: "kiosk" },
  },
  {
    kind: "page",
    path: "/kiosk/arena",
    element: KioskArena,
    layout: "none",
    gate: "public",
    discovery: { reason: "kiosk" },
  },
  {
    kind: "page",
    path: "/kiosk/arena/:locationSlug",
    element: KioskArena,
    layout: "none",
    gate: "public",
    discovery: { reason: "kiosk" },
  },
  {
    kind: "page",
    path: "/kiosk/:locationSlug",
    element: Kiosk,
    layout: "none",
    gate: "public",
    discovery: { reason: "kiosk" },
  },

  // ---------------------------------------------------
  // AUTHENTICATED — inside GuardedLayout (app shell)
  // Grouped by the pre-rail sidebar's sections, purely for readability
  // (rail order lives in discoveryRail.ts — see the comment atop this file).
  // ---------------------------------------------------

  // --- Rail destinations: Today (/) and its tabs (nav-ia rev2) ---
  {
    kind: "page",
    path: "/",
    // Role branch: staff → /my-schedule, restricted → /my-schedule,
    // privileged → /inbox (docs/plans/2026-08-22-lms-reframe.md).
    element: RootRoute,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Today", icon: LayoutDashboard } },
  },
  {
    kind: "page",
    path: "/inbox",
    element: VoorJouPage,
    layout: "app",
    gate: "privileged",
    // LMS reframe (docs/plans/2026-08-22-lms-reframe.md): Voor jou is the
    // manager landing (RootRoute sends privileged here) — first Today tab.
    discovery: { nav: { title: "For You", icon: Inbox, badge: "inbox", tabOf: "/" } },
  },
  {
    kind: "page",
    path: "/overzicht",
    element: Dashboard,
    layout: "app",
    gate: "privileged",
    // Title is "Dashboard" (not main's "Overzicht") — the rail requires
    // globally unique nav titles and /childcare/overzicht's tab holds the
    // Overview name.
    discovery: { nav: { title: "Dashboard", icon: LayoutDashboard, tabOf: "/" } },
  },
  {
    kind: "page",
    path: "/me",
    element: MyProfileRedirect,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: staffNav-only (target-tree.json `staffNavOnly`) — dropped
    // from the manager rail, kept on the teacher tree + /staff/:id shortcut.
    discovery: { reason: "staff-nav-only" },
    staffNav: { order: 11, section: "Account & help", title: "My Profile", icon: User },
  },
  {
    kind: "page",
    path: "/my-schedule",
    element: MySchedule,
    layout: "app",
    gate: "authenticated",
    // Teacher self-service home (staffNav) AND the last Today tab on the
    // manager rail — managers declare their own extra hours here too (owner
    // ruling 2026-09-23, docs/plans/2026-09-23-mijn-week-fidelity-and-managers.md).
    // Gate stays `authenticated`: self-scoped, same contract for every role.
    discovery: { nav: { title: "My Schedule", icon: CalendarClock, tabOf: "/" } },
    staffNav: { order: 1, section: "Today", title: "My Schedule", icon: CalendarClock },
  },
  {
    kind: "page",
    path: "/conversations",
    element: Conversations,
    layout: "app",
    // Teddy Conversations (docs/plans/2026-10-03-teddy-conversations.md). The
    // pilot gate is app_can_use_conversations(), not a RouteGate value: the
    // page checks it, and the edge function refuses every action without it.
    // Reached from the header entry and the My Schedule home card, never the
    // sidebar. Deliberately absent from the location and restricted
    // allowlists — a private conversation never runs on a shared session.
    gate: "authenticated",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/academy",
    element: AcademyHome,
    layout: "app",
    // Teddy Academy (docs/plans/2026-10-05-teddy-academy.md, Task 1): the
    // leadership sidebar's Academy section, not a rail destination. Absent from
    // the location and restricted allowlists, so those sessions are refused.
    // academy_get_today / RLS enforce Academy access server-side.
    gate: "academy",
    discovery: { reason: "academy-section" },
  },
  {
    kind: "page",
    path: "/academy/voice",
    element: VoiceHome,
    layout: "app",
    gate: "academy",
    discovery: { reason: "academy-section" },
  },
  {
    kind: "page",
    path: "/academy/voice/practice",
    element: VoicePractice,
    layout: "app",
    // Teddy Voice parent-reply practice (plan 2026-10-05-teddy-academy, Task
    // 5). Managers, office managers and coaches; reached from /academy/voice. Deliberately absent from
    // the location and restricted allowlists, like /conversations: what the
    // manager writes is a private conversation.
    gate: "academy",
    discovery: { reason: "drill-down" },
  },
  // 🎄 TK WRAPPED — HIDDEN UNTIL CHRISTMAS (re-enable ~Dec 2026, owner ask 2026-07-27).
  // To restore: uncomment this block + the WrappedPage lazy import above and
  // decide its rail placement (it predates the nav-ia rev2 flat rail — likely
  // a Today tab or staffNav-only), then re-add "TK Wrapped" to
  // Layout.test.tsx STAFF_NAV_TITLES.
  // {
  //   kind: "page",
  //   path: "/my-wrapped",
  //   element: WrappedPage,
  //   layout: "app",
  //   gate: "authenticated",
  //   // TK Wrapped (Play 2, docs/plans/2026-07-24-play1-pattern-miner.md
  //   // sequencing doc) — celebratory self-view, everyone with a linked staff
  //   // record sees their own year, same self-scope contract as My Schedule.
  //   discovery: { reason: "staff-nav-only" },
  //   staffNav: { order: 10, section: "Grow", title: "TK Wrapped", icon: Gift },
  // },
  {
    kind: "page",
    path: "/email",
    element: Email,
    layout: "app",
    // Unified onto the staff_roles world via useRouteGate (2026-07-10,
    // owner-authorized) — page/server ACL (useCurrentUserRole + the
    // gmail-threads server-side check) untouched, same humans qualify today.
    gate: "manager",
    // nav-ia rev2: Email channel tab under the Inbox destination.
    discovery: { nav: { title: "Email", icon: Mail, tabOf: "/parent-messages" } },
  },
  {
    kind: "page",
    path: "/oudervragen",
    element: ManagerParentQuestions,
    layout: "app",
    // Location managers' view of parent invoice questions on their own
    // locations (docs/plans/2026-10-13-oudervragen-manager-view.md). The
    // manager_list_parent_questions RPC is the real scope boundary; billing
    // managers keep answering in Finance · Oudervragen (/billing?tab=questions).
    gate: "manager",
    discovery: { nav: { title: "Oudervragen", icon: ReceiptText, tabOf: "/parent-messages" } },
  },
  {
    kind: "page",
    path: "/hub",
    element: Hub,
    layout: "app",
    // 'privileged' — RLS phase2-final Task A2: teachers (role === "staff")
    // lose /hub; manager/admin/coach unaffected. Page self-enforces via
    // useRouteGate("privileged"), same pattern as LeadershipKPI's 'manager'.
    gate: "privileged",
    discovery: { nav: { title: "Hub", icon: MessageSquarePlus, tabOf: "/" } },
  },
  {
    kind: "page",
    path: "/work-planning",
    element: WorkPlanning,
    layout: "app",
    gate: "authenticated",
    staffNav: { order: 3.5, section: "Today", title: "Work planning", icon: ListTodo },
    discovery: { nav: { title: "Work planning", icon: ListTodo } },
  },
  {
    kind: "page",
    path: "/parent-requests",
    element: ParentRequests,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: hosts the /no-shows and /drift-signalen pills (both lose
    // their own nav entries until the hub page grows real in-page tabs).
    discovery: { nav: { title: "Requests & Queues", icon: Bell, badge: "parentRequests", tabOf: "/" } },
  },
  {
    kind: "page",
    path: "/no-shows",
    element: NoShowQueue,
    layout: "app",
    // Manager-only (stricter than /parent-requests) — cross-child data:
    // every row on this page belongs to a different family.
    gate: "manager",
    // nav-ia rev2: counted pill of the Requests & Queues tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/drift-signalen",
    element: DriftSignalsPage,
    layout: "app",
    // Manager-only — cross-child aggregate, same class as /no-shows.
    gate: "manager",
    // nav-ia rev2: counted pill of the Requests & Queues tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/parent-messages",
    element: ParentMessages,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: the Inbox rail destination — Berichten is its default
    // surface, /messages + /email are its channel tabs.
    discovery: { nav: { title: "Inbox", icon: MessageCircle, badge: "parentMessages" } },
    staffNav: { order: 3, section: "Today", title: "Messages", icon: MessageCircle },
  },
  ...(PARENT_MESSAGE_TOPICS_ENABLED ? [{
    kind: "page" as const,
    path: "/parent-message-topics",
    element: ParentMessageTopics,
    layout: "app" as const,
    gate: "managerOrHrAdmin" as const,
    discovery: { nav: { title: "Berichtenthema's", icon: BarChart3, tabOf: "/parent-messages" } },
  }] : []),
  {
    kind: "page",
    path: "/messages",
    element: MessageCenter,
    layout: "app",
    // Manager-tier: intake/sales-funnel surface for managers, not a daily
    // parent-contact tool like /parent-messages (docs/plans/
    // 2026-07-26-messages-nav-consolidation.md). Gate enforced in nav AND at
    // the page (MessageCenter wrapper) — but RLS (contact_conversations/
    // contact_messages) still admits any authenticated staff row; that data
    // gap is accepted for v1, see the plan's Task 2 note.
    gate: "manager",
    // nav-ia rev2: Website Contact channel tab under Inbox — the deliberate
    // 2026-07-26 split survives as channel separation, not separate menus.
    discovery: { nav: { title: "Website Contact", icon: MessageCircle, badge: "contactMessages", tabOf: "/parent-messages" } },
  },
  {
    kind: "page",
    path: "/parent-content",
    element: ParentContent,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Parents", icon: FileText } },
  },
  {
    kind: "page",
    path: "/events",
    element: Events,
    layout: "app",
    // Privileged (teacher-launch-hardening: visible to office_manager + coach,
    // teacher-launch-hardening Task 4 baseline still excludes teachers) — no
    // staffNav entry; teachers redirect to /me via RouteGateGuard.
    gate: "privileged",
    discovery: { nav: { title: "Events", icon: CalendarDays, tabOf: "/parent-content" } },
  },
  {
    kind: "page",
    path: "/snaps",
    element: SnapsPage,
    layout: "app",
    // 'authenticated' since 20261027210200: teachers match the batches of
    // their own location (staff_snap_list_sessions / snap_match_session are
    // the real boundary); sessions, batches and announce stay manager-only
    // server-side and the page hides them for non-managers.
    gate: "authenticated",
    discovery: { nav: { title: "TeddySnaps", icon: Camera, tabOf: "/parent-content" } },
    staffNav: { order: 4.5, section: "Children & planning", title: "TeddySnaps", icon: Camera },
  },
  {
    kind: "page",
    path: "/snaps/:sessionId/match",
    element: SnapMatchPage,
    layout: "app",
    // Foto's matchen (docs/plans/2026-10-18-snaps-photo-match.md): managers
    // and, since 20261027210200, teachers of the batch's location link photos
    // to children. The match RPCs enforce staff + location.
    gate: "authenticated",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/issues",
    element: IssuesPage,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Issues", icon: AlertCircle, badge: "issues", tabOf: "/" } },
  },
  {
    kind: "page",
    path: "/issues/submit",
    element: IssuesSubmit,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: pill of the Issues tab; staffNav keeps pointing teachers
    // straight at it.
    discovery: { reason: "rail-pill" },
    staffNav: { order: 12, section: "Account & help", title: "Issues", icon: AlertCircle },
  },
  {
    kind: "page",
    path: "/issues/dashboard",
    element: IssuesDashboard,
    layout: "app",
    gate: "manager",
    // No separate nav entry — reached via a link inside /issues.
    discovery: { reason: "drill-down" },
  },

  // --- People ---
  {
    kind: "page",
    path: "/staff",
    element: StaffPage,
    layout: "app",
    // 'privileged' — RLS phase2-final Task A2: teachers (role === "staff")
    // lose the directory list; manager/admin/coach unaffected. Page
    // self-enforces via useRouteGate("privileged").
    gate: "privileged",
    discovery: { nav: { title: "Teachers", icon: Users, badge: "staff" } },
  },
  {
    // Sickness overview (docs/plans/2026-07-27-sickness-overview.md): ranked
    // who's-sick-most view over approved sick leave. managerOrHrAdmin —
    // same composite as Verlofcentrum, sensitive cross-staff health data.
    kind: "page",
    path: "/sickness-overview",
    element: SicknessOverviewPage,
    layout: "app",
    gate: "managerOrHrAdmin",
    // nav-ia rev2: Sickness & UWV pill of the Verlofcentrum tab — its badge
    // key is carried by that tab entry (/childcare/leave).
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/staff/:id",
    element: StaffProfile,
    layout: "app",
    // 'authenticated' — NOT 'privileged'. MyProfileRedirect (/me) sends
    // teachers to /staff/:id for their own profile; a 'privileged' gate here
    // would deny plain "staff" role and bounce back to /me, an infinite
    // redirect loop (teacher-launch-hardening Task 4 fix). StaffProfile
    // itself still self-guards: privileged viewers OR isOwnProfile (compares
    // :id to useCurrentUserStaff().id) get in, everyone else sees
    // RouteGateDenied — see StaffProfile.tsx ~line 239-260.
    gate: "authenticated",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/interns",
    element: Interns,
    layout: "app",
    // 'privileged' — RLS phase2-final intern_manager task: hidden from plain
    // staff, visible to manager/admin/coach and the new intern_manager role.
    // Page self-enforces via useRouteGate("privileged").
    gate: "privileged",
    discovery: { nav: { title: "Interns", icon: GraduationCap, badge: "interns", tabOf: "/staff" } },
  },
  {
    // Menu reorg phase 2: Payroll is now a gate-guarded tab on Interns
    // (/interns?tab=payroll) — see Interns.tsx. Direct /interns/payroll
    // links keep working via this redirect.
    kind: "redirect",
    path: "/interns/payroll",
    redirectTo: "/interns?tab=payroll",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/talent",
    redirectTo: "/hiring",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/hiring",
    element: HiringPage,
    layout: "app",
    // 'hiring' — RLS phase2-final intern_manager task: admin/manager/coach,
    // mirroring the candidates RLS policies. Page self-enforces via
    // useRouteGate("hiring").
    gate: "hiring",
    // nav-ia rev2: the Intake rail destination — one inbound umbrella for
    // staff recruitment + child admissions (owner directive; domain mismatch
    // flagged in docs/plans/nav-ia/proposal.md).
    discovery: { nav: { title: "Intake", icon: UserPlus } },
  },
  {
    kind: "redirect",
    path: "/registrations",
    redirectTo: "/admissions",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/tours",
    redirectTo: "/admissions",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/admissions",
    element: Tours,
    layout: "app",
    // Re-gated authenticated -> privileged (P0 launch fix, PR #919): early
    // admissions-kanban stages (lead/interested/touring) commonly have no
    // placement row yet at all, which can never pass the staff-assignment-
    // scoped children RLS policy for a non-elevated caller. Managers/admins/
    // coaches keep full access via the same RLS elevated bypass.
    gate: "privileged",
    // Raw title stays lowercase "admissions" — displayed via i18n
    // (t('nav.admissions')). Kept verbatim.
    discovery: { nav: { title: "admissions", icon: ClipboardList, tabOf: "/hiring" } },
  },

  // --- Games ---
  {
    kind: "page",
    path: "/games",
    element: Games,
    layout: "app",
    gate: "authenticated",
    // exact: true — /games has children (/games/battle, /games/console, ...);
    // without it every sub-route would also highlight "Teddy Games" as active.
    discovery: { nav: { title: "Teddy Games", icon: Gamepad2, exact: true, tabOf: "/grow" } },
    staffNav: { order: 9, section: "Grow", title: "Teddy Games", icon: Gamepad2 },
  },
  {
    kind: "page",
    path: "/games/battle",
    element: GamesBattle,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: inner pill of the Teddy Games tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/games/console",
    element: GamesConsole,
    layout: "app",
    // staff_roles manager tier — mirrors GamesConsole.tsx's own useCurrentUserStaff
    // gate and Layout.tsx's staffManagerOnly nav flag (not the generic adminOnly
    // filter, which reads user_roles instead).
    gate: "manager",
    // nav-ia rev2: manager/admin pill of the Teddy Games tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/games/submit/:questId",
    element: QuestSubmitPage,
    layout: "app",
    gate: "authenticated",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/games/launch",
    element: GamesLaunch,
    layout: "app",
    gate: "manager",
    // nav-ia rev2: print action inside the console's Season Config, never a
    // nav entry (target-tree.json merge-into:/games/console).
    discovery: { reason: "rail-pill" },
  },

  // --- Sint's Big Book (docs/plans/2026-10-14-sinterklaas-big-book.md) ---
  {
    kind: "page",
    path: "/sinterklaas",
    element: Sinterklaas,
    layout: "app",
    // Sint's Big Book (docs/plans/2026-10-14-sinterklaas-big-book.md): the
    // parent entries are management-only. RLS (sinterklaas_can_manage():
    // manager, super_admin, office_manager, admin, coach) is the real gate.
    gate: "privileged",
    // Polls has no rail entry (it is a view inside the standalone Work
    // planning page, which keeps no tabs), so this parent campaign sits with
    // the other parent-facing surfaces (Events, TeddySnaps).
    discovery: { nav: { title: "Sinterklaas", icon: Gift, tabOf: "/parent-content" } },
  },

  // --- Lootjes (Secret Santa, docs/plans/2026-10-07-lootjes.md) ---
  {
    kind: "page",
    path: "/lootjes",
    element: Lootjes,
    layout: "app",
    // Every staff member draws; organizer features render for managers only
    // (useCurrentUserStaff role, RLS app_is_manager()). Location accounts see
    // a "Log in als medewerker" state inside the page.
    gate: "authenticated",
    // exact: true — /lootjes/:id must not double-highlight the tab.
    discovery: { nav: { title: "Lootjes", icon: Gift, exact: true, tabOf: "/grow" } },
    staffNav: { order: 9.5, section: "Grow", title: "Lootjes", icon: Gift },
  },
  {
    kind: "page",
    path: "/lootjes/:id",
    element: LootjesGroup,
    layout: "app",
    gate: "authenticated",
    discovery: { reason: "drill-down" },
  },

  // --- Childcare ---
  {
    kind: "page",
    path: "/children",
    element: ChildrenPage,
    layout: "app",
    gate: "authenticated",
    // exact: true — /children/leavers is a sibling route with its own rail
    // tab; without exact, startsWith prefix-matching double-highlights both
    // Children and Leavers. /children/:id (drill-down, no nav item) is
    // unaffected since it never appears on the rail.
    discovery: { nav: { title: "Children", icon: Baby, exact: true } },
    staffNav: { order: 4, section: "Children & planning", title: "Children", icon: Baby },
  },
  {
    kind: "page",
    path: "/children/leavers",
    element: LeaversPage,
    layout: "app",
    // Re-gated authenticated -> privileged (P0 launch fix, PR #919): leavers
    // have an ended placement, which can never pass the staff-assignment-
    // scoped children RLS policy for a non-elevated caller (that policy
    // requires an active/upcoming placement) — the page would show zero
    // leavers, always, not narrower results. Managers/admins/coaches keep
    // full access via the same RLS elevated bypass.
    gate: "privileged",
    discovery: { nav: { title: "Leavers", icon: UserCheck, tabOf: "/children" } },
  },
  {
    kind: "page",
    path: "/children/:id",
    element: ChildProfilePage,
    layout: "app",
    gate: "authenticated",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/grid",
    element: OperationsGridPage,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Grid", icon: LayoutGrid } },
    staffNav: { order: 2, section: "Today", title: "Grid", icon: LayoutGrid },
  },
  {
    kind: "page",
    path: "/activities",
    element: ActivitiesPage,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Activities", icon: Sparkles } },
    staffNav: { order: 2.5, section: "Today", title: "Activities", icon: Sparkles },
  },
  {
    kind: "redirect",
    path: "/childcare/grid",
    redirectTo: "/grid",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/childcare/operations",
    redirectTo: "/grid",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/childcare/planning",
    element: ControlRoomPage,
    layout: "app",
    // 'privileged' — sick-masking-calendar-guard Task 3: hosts LeavePanel
    // (full leave type + note + edit/delete affordances) and org-wide
    // control-room tooling; was 'authenticated', which let any teacher token
    // reach it (#907's "week plan = managers only" ruling was violated).
    gate: "privileged",
    discovery: { nav: { title: "Planning", icon: Kanban } },
  },
  {
    kind: "page",
    path: "/childcare/staffing-horizon",
    element: StaffingHorizonPage,
    layout: "app",
    gate: "privileged",
    discovery: { nav: { title: "Staffing Horizon", icon: CalendarRange, tabOf: "/childcare/planning" } },
  },
  {
    kind: "page",
    path: "/childcare/floorplan",
    element: FloorplanPage,
    layout: "app",
    // Managers-only per the dossier's V1 cut (docs/plans/2026-07-24-play3-living-floorplan.md)
    // — page self-enforces via useRouteGate("manager"), same pattern as /games/console.
    gate: "manager",
    discovery: { nav: { title: "Live Floorplan", icon: Building2, tabOf: "/childcare/planning" } },
  },
  {
    // Planning hub (docs/plans/2026-07-25-planning-hub.md): one page
    // composing Weekoverzicht/Prognose/Herverdelen as tabs — now the
    // Overview tab of the Planning rail destination (nav-ia rev2), hosting
    // /childcare/substitute-loop as a future in-page pill.
    kind: "page",
    path: "/childcare/overzicht",
    element: PlanningHubPage,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Overview", icon: CalendarRange, tabOf: "/childcare/planning" } },
  },
  {
    // Route kept alive for deep links / muscle memory; nav entry removed —
    // superseded by the Weekoverzicht tab on /childcare/overzicht. Same
    // redirect-with-tab pattern as /childcare/leave-rules below.
    kind: "redirect",
    path: "/childcare/week-overview",
    redirectTo: "/childcare/overzicht?tab=weekoverzicht",
    discovery: { reason: "redirect" },
  },
  {
    // nav-ia rev2: literal duplicate of the hub's Herverdelen tab (same
    // OverplanningView) — redirect, exactly like week-overview and
    // bezettingsradar before it. The OverplanningRadarPage wrapper is gone;
    // the hub renders OverplanningView directly.
    kind: "redirect",
    path: "/childcare/overplanning",
    redirectTo: "/childcare/overzicht?tab=herverdelen",
    discovery: { reason: "redirect" },
  },
  {
    // Route kept alive for deep links / muscle memory; nav entry removed —
    // superseded by the Prognose tab on /childcare/overzicht.
    kind: "redirect",
    path: "/childcare/bezettingsradar",
    redirectTo: "/childcare/overzicht?tab=prognose",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/childcare/week-plan",
    element: WeekPlanEditorPage,
    layout: "app",
    // Privileged (teacher-launch-hardening: visible to office_manager + coach,
    // teacher-launch-hardening Task 4 baseline still excludes teachers) — no
    // staffNav entry; teachers redirect to /me via RouteGateGuard.
    gate: "privileged",
    discovery: { nav: { title: "Week Plan", icon: CalendarDays, tabOf: "/childcare/planning" } },
  },
  {
    kind: "page",
    path: "/schools",
    element: SchoolsCalendarPage,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Schools", icon: School, tabOf: "/children" } },
    staffNav: { order: 5, section: "Children & planning", title: "Schools", icon: School },
  },
  {
    kind: "page",
    path: "/childcare/auto-plan",
    element: AutoPlanPage,
    layout: "app",
    gate: "manager",
    // nav-ia rev2: Generate mode/pill of the Week Plan tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/childcare/leave-sync",
    element: LeaveSyncPage,
    layout: "app",
    // 'managerOrHrAdmin' — sick-masking-calendar-guard Task 3: hosts
    // LeaveRequestsSection (all-staff pending/decided + approve/reject, no
    // client-side role check of its own — server RLS update policy is
    // is_manager_or_admin() only). Was 'authenticated'.
    gate: "managerOrHrAdmin",
    // nav-ia rev2: Vera sync pill of the Verlofcentrum tab.
    discovery: { reason: "rail-pill" },
  },
  {
    // Menu reorg phase 2: Leave Rules is now a tab on Verlofcentrum
    // (/childcare/leave?tab=rules) — see VerlofcentrumPage.tsx. Both are
    // 'hrAdmin', so no internal per-tab guard was needed. Direct
    // /childcare/leave-rules links keep working via this redirect.
    kind: "redirect",
    path: "/childcare/leave-rules",
    redirectTo: "/childcare/leave?tab=rules",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/childcare/leave",
    element: VerlofcentrumPage,
    layout: "app",
    gate: "managerOrHrAdmin",
    // exact: true — /childcare/leave is a prefix of the sibling route
    // /childcare/leave-sync; without it the Verlofcentrum tab lights up on
    // that page too.
    // nav-ia rev2: Verlofcentrum tab of Uren & Verlof, hosting leave-sync
    // (Vera sync) and /sickness-overview (Sickness & UWV) as pills — it
    // carries the sicknessOverview badge key the pill's nav entry had.
    discovery: {
      nav: { title: "Leave Center", icon: Umbrella, exact: true, badge: "sicknessOverview", tabOf: "/childcare/hours" },
    },
  },
  {
    // Staff vacations grid (docs/plans/2026-09-02-staff-vacations-grid.md):
    // staff x day leave timeline over the same planning.staff_leave rows the
    // Leave Center lists — a sibling tab of Uren & Verlof, same
    // managerOrHrAdmin audience (cross-staff leave data).
    kind: "page",
    path: "/childcare/vacations",
    element: StaffVacationsPage,
    layout: "app",
    gate: "managerOrHrAdmin",
    discovery: { nav: { title: "Staff vacations", icon: CalendarRange, tabOf: "/childcare/hours" } },
  },
  {
    // Radar Hub — 8 live operational radars + Actiebord (cross-radar action
    // board). docs/plans/2026-08-08-radar-hub.md. managerOrHrAdmin mirrors
    // Verlofcentrum above (same audience: cross-staff/cross-child ops data).
    kind: "page",
    path: "/radar",
    element: RadarHubPage,
    layout: "app",
    gate: "managerOrHrAdmin",
    // nav-ia rev2: Radar tab of the Planning destination, hosting
    // /childcare/portabase-changes as a pill.
    discovery: { nav: { title: "Radar", icon: Radar, tabOf: "/childcare/planning" } },
  },
  {
    kind: "page",
    path: "/childcare/substitute-loop",
    element: SubstituteLoopPage,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: Substitutes pill of the Overview tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/childcare/hours",
    element: TeacherHoursPage,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: the Uren & Verlof rail destination — its staff-visible
    // My Hours view makes it the right all-staff entry (target-tree.json).
    discovery: { nav: { title: "Hours & Leave", icon: Clock } },
    staffNav: { order: 6, section: "Children & planning", title: "Hours", icon: Clock },
  },
  {
    kind: "page",
    path: "/childcare/signalen",
    element: SignalsPage,
    layout: "app",
    gate: "managerOrHrAdmin",
    // nav-ia rev2: hosts /childcare/patronen as its exploratory pill.
    discovery: { nav: { title: "Signals", icon: Signal, tabOf: "/childcare/hours" } },
  },
  {
    kind: "page",
    path: "/childcare/patronen",
    element: PatternsPage,
    layout: "app",
    gate: "managerOrHrAdmin",
    // nav-ia rev2: exploratory pill of the Signals tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/childcare/hours-requests",
    element: HoursRequestsPage,
    layout: "app",
    gate: "manager",
    discovery: { nav: { title: "Hours Changes", icon: FileClock, badge: "hoursRequests", tabOf: "/childcare/hours" } },
  },
  {
    // Reiskosten payroll month (docs/plans/2026-09-28-reiskosten-km.md Task 5):
    // every colleague's km-days, km and € for one month + the Employes CSV.
    // managerOrHrAdmin mirrors the RPC's app_can_manage_reiskosten().
    kind: "page",
    path: "/planning/reiskosten",
    element: ReiskostenMonthPage,
    layout: "app",
    gate: "managerOrHrAdmin",
    discovery: { nav: { title: "Reiskosten", icon: Car, tabOf: "/childcare/hours" } },
  },
  {
    // Teddy Gala RSVP tally (docs/plans/2026-09-28-teddy-gala-rsvp.md Task 3):
    // counts, names and decline reasons + CSV. manager mirrors the RPC's
    // app_is_manager(); reached from the link under the My Schedule card.
    kind: "page",
    path: "/planning/gala-rsvp",
    element: GalaRsvpOverviewPage,
    layout: "app",
    gate: "manager",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/childcare/portabase-changes",
    element: PortabaseChangesPage,
    layout: "app",
    gate: "manager",
    // nav-ia rev2: rail-pill of the Radar tab.
    discovery: { reason: "rail-pill" },
  },

  // --- Analytics ---
  {
    kind: "page",
    path: "/daily-logs",
    element: KioskWeekGrid,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Daily Logs", icon: Thermometer, tabOf: "/children" } },
  },
  {
    kind: "page",
    path: "/activity",
    element: ActivityFeed,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: Activiteit pill of the Reviews tab.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "redirect",
    path: "/insights",
    redirectTo: "/",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/reports",
    element: Reports,
    layout: "app",
    // super_admin semantics via useSuperAdminGate (owner OR staff_roles
    // super_admin, which useCurrentUserStaff collapses to role === 'admin').
    gate: "admin",
    discovery: { nav: { title: "TK Stats", icon: BarChart3, tabOf: "/cockpit" } },
  },
  {
    kind: "page",
    path: "/cockpit",
    element: CockpitOverview,
    layout: "app",
    gate: "owner",
    // nav-ia rev2: the Insight rail destination — the Leadership KPIs tab
    // (manager gate) widens it beyond owner/admin (target-tree.json).
    discovery: { nav: { title: "Insight", icon: BarChart3 } },
  },
  {
    kind: "page",
    path: "/cockpit/financials",
    element: CockpitFinancials,
    layout: "app",
    // Owner + the bookkeeper (owner ruling 2026-10-07); see accountantAllowlist.
    gate: "ownerOrAccountant",
    // No separate nav entry — reached from within /cockpit.
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/leadership",
    element: LeadershipKPI,
    layout: "app",
    // Unified onto the staff_roles world (2026-07-10, owner-authorized): the
    // page's own guard now mirrors GamesConsole's staff-manager predicate
    // (useCurrentUserStaff) instead of useCurrentUserRole (user_roles). Same
    // humans qualify today (leadership = admin OR manager), now on one gate
    // source everywhere.
    gate: "manager",
    discovery: { nav: { title: "Leadership KPIs", icon: TrendingUp, tabOf: "/cockpit" } },
  },

  // --- Operations ---
  {
    kind: "page",
    path: "/reviews",
    element: Reviews,
    layout: "app",
    // Manager+ only: this page is the manager review-management UI (Complete
    // Review, staff picker, manager-private assessment). It has no per-user
    // scoping, so an employee reaching it saw manager-shaped tooling and their
    // own manager-private assessment. A read-only self-review view is separate
    // future work.
    gate: "privileged",
    discovery: { nav: { title: "Reviews", icon: Star, tabOf: "/staff" } },
  },
  {
    kind: "page",
    path: "/onboarding",
    element: OnboardingAdmin,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: Team pill of the Onboarding tab (/grow/onboarding).
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/compliance",
    element: ComplianceDashboard,
    layout: "app",
    // 'privileged' — RLS phase2-final Task A2: teachers (role === "staff")
    // lose /compliance; manager/admin/coach unaffected. Page self-enforces
    // via useRouteGate("privileged").
    gate: "privileged",
    // exact: true — /compliance/document-types is a sibling route; without
    // it, visiting Document Types would also highlight "Compliance".
    // nav-ia rev2: hosts /compliance/document-types as its admin-only pill.
    discovery: { nav: { title: "Compliance", icon: ShieldCheck, exact: true, tabOf: "/staff" } },
  },
  {
    kind: "page",
    path: "/children/photo-flags",
    element: PhotoFlagsPage,
    layout: "app",
    gate: "manager",
    // nav-ia rev2: moderation pill of the Adoption & Photos tab.
    discovery: { reason: "rail-pill" },
  },
  {
    // B1 parent-app adoption panel (chunk L1) — per-family Nest usage,
    // read-only, manager-gated. Backed by the staff_parent_app_adoption()
    // RPC (RLS on parent_access_tokens is admin-only; see the migration's
    // header comment).
    kind: "page",
    path: "/parent-adoption",
    element: ParentAdoptionPanel,
    layout: "app",
    gate: "manager",
    // nav-ia rev2: hosts /children/photo-flags as its moderation pill.
    discovery: { nav: { title: "Adoption & Photos", icon: Smartphone, tabOf: "/parent-content" } },
  },

  // --- Billing ---
  // Finance Command Center hub (docs/plans/2026-08-24-finance-command-center.md
  // Task 1): /billing hosts FinanceHubPage — the existing Command Center
  // content as the default view plus 8 lazy tabs composing the absorbed
  // finance pages (see src/pages/finance/financeTabs.ts). Every absorbed
  // route below is a static redirect to its hub deep link; the two
  // drill-downs (/billing/families/:debtorNumber,
  // /billing/invoices/:invoiceNumber) stay live pages.
  {
    kind: "page",
    path: "/billing",
    element: FinanceHubPage,
    layout: "app",
    // 'privileged' (not plain staff): company invoices are a manager/ops
    // job. Teachers reimburse personal spends at /declaraties, not here.
    // RouteGateGuard bounces teachers to /me. Location managers still
    // enter (privileged); billing-shaped tabs keep their own page guards.
    gate: "privileged",
    discovery: { nav: { title: "Finance", icon: BarChart3 } },
  },
  // Absorbed into the hub's Bank & Payments tab (?tab=bank-payments). The
  // route's billingManager gate semantics live on with the tab page's own
  // useCanManageBilling guard.
  {
    kind: "redirect",
    path: "/billing/payments",
    redirectTo: "/billing?tab=bank-payments",
    discovery: { reason: "redirect" },
  },
  // Absorbed into the Invoices tab as its Open pill (?tab=invoices&pill=open)
  // — Finance Command Center Task 3 deleted BillingOpenInvoices.tsx.
  {
    kind: "redirect",
    path: "/billing/open-invoices",
    redirectTo: "/billing?tab=invoices&pill=open",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/billing/runs",
    redirectTo: "/billing?tab=invoice-runs",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/billing/tarieven",
    redirectTo: "/billing?tab=rates",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/billing/families/:debtorNumber",
    element: BillingFamilyAccount,
    layout: "app",
    // Mirrors app_can_manage_billing() — drill-down, untouched by the hub.
    gate: "billingManager",
    discovery: { reason: "drill-down" },
  },
  // Books bridge: ledger (Nmbrs Accounting) vs LMS tie-out. Same gate as the
  // hub's AP Bank tab; the hub's Books tab links here.
  {
    kind: "page",
    path: "/billing/books",
    element: BillingBooks,
    layout: "app",
    gate: "moneyFlows",
    discovery: { reason: "drill-down" },
  },
  // Absorbed into the hub's Invoices tab (?tab=invoices).
  {
    kind: "redirect",
    path: "/billing/invoices",
    redirectTo: "/billing?tab=invoices",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/billing/invoices/:invoiceNumber",
    element: BillingInvoiceDetail,
    layout: "app",
    // Mirrors app_can_manage_billing() — drill-down, untouched by the hub.
    gate: "billingManager",
    discovery: { reason: "drill-down" },
  },

  // --- Payables ---
  // AP Phase 5 Task A2 (docs/plans/2026-08-02-ap-phase5-plan.md) merged the
  // payables menu 6→3; the Finance hub (Task 1) now absorbs those routes
  // outright — each becomes a redirect to its hub tab. The former
  // /payables/flags|match|inbox legacy redirects are retargeted to the same
  // hub equivalents, so no bookmark 404s.
  {
    kind: "redirect",
    path: "/payables",
    redirectTo: "/billing?tab=payables",
    discovery: { reason: "redirect" },
  },
  // Absorbed into the hub's Expenses tab — company supplier invoices, not
  // teacher reimbursements (those are /declaraties). No teacher staffNav.
  {
    kind: "redirect",
    path: "/payables/submit",
    redirectTo: "/billing?tab=expenses",
    discovery: { reason: "redirect" },
  },
  // Absorbed into the Expenses tab as its Mijn imports pill
  // (?tab=expenses&pill=imports — the pill renders PersonalImportsPanel from
  // the PayablesSubmit tab page; imports stay RLS-private even from the
  // owner, tab colocation only). No teacher staffNav.
  {
    kind: "redirect",
    path: "/payables/personal",
    redirectTo: "/billing?tab=expenses&pill=imports",
    discovery: { reason: "redirect" },
  },
  // Declaraties Phase 1 Task 3 (docs/plans/2026-08-26-declaraties.md) —
  // teacher expense claims, deliberately separate from the AP/finance stack.
  // gate 'authenticated' (every staff member declares; approver visibility is
  // data-level via RLS + declaratie_approvers — no new gate value, spec AC).
  // staffNav under Account & help next to My Profile/Issues (plan
  // low-confidence #2); NOT on the manager rail — approvals land in Voor jou
  // (Phase 2), never as a rail sibling.
  {
    kind: "page",
    path: "/declaraties",
    element: DeclaratiesPage,
    layout: "app",
    gate: "authenticated",
    discovery: { reason: "staff-nav-only" },
    staffNav: { order: 15, section: "Account & help", title: "Declaraties", icon: ReceiptEuro },
  },
  // Shared by link (WhatsApp/mail to the core team); no rail or staffNav entry.
  // Gate stays `authenticated` so a non-core-team login sees the "for the core
  // team" message instead of a redirect.
  {
    kind: "page",
    path: "/manager-protocol",
    element: ManagerProtocol,
    layout: "app",
    gate: "authenticated",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "redirect",
    path: "/payables/review",
    redirectTo: "/billing?tab=ap-review",
    discovery: { reason: "redirect" },
  },
  // 'moneyFlows' (super_admin/accountant) tab — the gate lives on with the
  // tab registry (src/pages/finance/financeTabs.ts) and the tab page's own
  // hooks; office_manager never gets the bank feed/imports/matching.
  {
    kind: "redirect",
    path: "/payables/bank",
    redirectTo: "/billing?tab=ap-bank",
    discovery: { reason: "redirect" },
  },
  // Old URLs (Task A2 6→3 reorg) — retargeted to the same hub equivalents
  // the routes they pointed at now redirect to. Redirects carry no gate
  // (RouteGateGuard short-circuits on `kind: "redirect"`, src/App.tsx); the
  // target's own gate applies once there.
  {
    kind: "redirect",
    path: "/payables/flags",
    redirectTo: "/billing?tab=payables",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/payables/match",
    redirectTo: "/billing?tab=ap-bank",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/payables/inbox",
    redirectTo: "/billing?tab=ap-review",
    discovery: { reason: "redirect" },
  },

  // --- System ---
  {
    kind: "page",
    path: "/employes-sync",
    element: EmployesSync,
    layout: "app",
    gate: "admin",
    discovery: { nav: { title: "Employes Sync", icon: RefreshCw, tabOf: "/settings" } },
  },
  {
    kind: "page",
    path: "/compliance/document-types",
    element: DocumentTypesAdmin,
    layout: "app",
    gate: "admin",
    // nav-ia rev2: config pill of the Compliance tab — stays admin-only.
    discovery: { reason: "rail-pill" },
  },
  {
    kind: "page",
    path: "/bug-reports",
    element: BugReportsCenter,
    layout: "app",
    gate: "admin",
    discovery: { nav: { title: "Bug Reports", icon: Bug, tabOf: "/settings" } },
  },
  // Nest Launch Cockpit — TEMPORARY staff-only page verifying the parent-app
  // rollout (docs/plans/2026-07-27-launch-polish-pack.md Task 4). No obvious
  // dev/labs nav section exists to attach this to (Labs 2.0 subtree was
  // deleted 2026-07-19, see the redirect entries below) — route-only per
  // owner instruction, reached by URL, not the sidebar. Remove this route
  // (and src/pages/launch/*, src/services/launch/*, src/hooks/launch/*) once
  // the rollout is verified and this page is no longer needed.
  {
    kind: "page",
    path: "/launch-cockpit",
    element: LaunchCockpit,
    layout: "app",
    gate: "admin",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/settings",
    element: SettingsPage,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Admin", icon: SettingsIcon } },
  },

  // --- Grow ---
  {
    kind: "page",
    path: "/grow",
    element: GrowHome,
    layout: "app",
    gate: "authenticated",
    // exact: true — /grow prefixes /grow/knowledge and /grow/onboarding.
    discovery: { nav: { title: "Grow", icon: Brain, exact: true } },
    ...(TEDDYVERSE_ENABLED ? { staffNav: { order: 6.5, section: "Grow" as const, title: "My Teddyverse", icon: Sparkles } } : {}),
  },
  {
    kind: "page",
    path: "/grow/knowledge",
    element: KnowledgePage,
    layout: "app",
    gate: "authenticated",
    discovery: { nav: { title: "Knowledge Center", icon: Brain, tabOf: "/grow" } },
    staffNav: { order: 7, section: "Grow", title: "Knowledge Center", icon: Brain },
  },
  {
    kind: "page",
    path: "/grow/knowledge/:slug",
    element: DocumentReaderPage,
    layout: "app",
    gate: "authenticated",
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/grow/onboarding",
    element: OnboardingPage,
    layout: "app",
    gate: "authenticated",
    // nav-ia rev2: hosts /onboarding (the admin overview) as its Team pill.
    discovery: { nav: { title: "Onboarding", icon: GraduationCap, tabOf: "/grow" } },
    staffNav: { order: 8, section: "Grow", title: "Onboarding", icon: GraduationCap },
  },
  {
    kind: "page",
    path: "/grow/coaching",
    element: Coaching,
    layout: "app",
    gate: "coaching",
    discovery: { nav: { title: "Coaching", icon: ClipboardList, tabOf: "/grow" } },
  },

  // --- Off-rail (drill-downs / legacy redirects — placement here is
  // arbitrary) ---
  {
    kind: "redirect",
    path: "/contracts/*",
    redirectTo: "/staff",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/generate-contract",
    redirectTo: "/staff",
    discovery: { reason: "redirect" },
  },
  {
    kind: "page",
    path: "/admin/journey-household-test",
    element: JourneyHouseholdTest,
    layout: "app",
    gate: "admin",
    discovery: { reason: "external-entry" },
  },
  {
    kind: "page",
    path: "/admin/journey",
    element: JourneyOps,
    layout: "app",
    gate: "admin",
    // Owner-approved 2026-07-10: nav entry added (was a NAV-GAP orphan from
    // the journey track's #522/#523). Page self-enforces the same 'admin'
    // gate via useRouteGate — nav visibility ≡ page admission.
    discovery: { nav: { title: "Journey Ops", icon: Waypoints, tabOf: "/parent-content" } },
  },
  {
    kind: "page",
    path: "/planning",
    element: Planning,
    layout: "app",
    // 'privileged' — #907's "planning = managers only" ruling flipped every
    // /childcare/* sibling but missed this drill-down page (no nav entry, so
    // nobody noticed). Closed 2026-07-30 with the absence_records RLS lock:
    // a teacher hand-typing /planning would otherwise see an absence-blind
    // org plan.
    gate: "privileged",
    // No nav entry — reached via "See full plan" links from dashboard cards
    // (BriefBanner, TodaysPlanCard).
    discovery: { reason: "drill-down" },
  },
  {
    kind: "page",
    path: "/employment-journey/:staffId",
    element: EmploymentJourney,
    layout: "app",
    gate: "authenticated",
    // Reached from ComplianceJourneyDrawer's "Open full history" link.
    discovery: { reason: "drill-down" },
  },
  // Labs 2.0 pages deleted (mock UI); the subtree survives only to redirect
  // legacy links home / into /hiring. LabsLayout (the trivial wrapper
  // component) was removed with it — the whole /labs/* subtree, including
  // any unmatched deep link, now redirects straight to "/".
  {
    kind: "redirect",
    path: "/labs/*",
    redirectTo: "/",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/labs",
    redirectTo: "/",
    discovery: { reason: "redirect" },
  },
  {
    kind: "redirect",
    path: "/labs/talent",
    redirectTo: "/hiring",
    discovery: { reason: "redirect" },
  },

  // ---------------------------------------------------
  // SHARED FIX-TODO CHECKLIST (docs/plans/2026-08-09-fix-todo-page.md)
  // No sidebar entry — reached via a link shared directly with managers.
  // ---------------------------------------------------
  {
    kind: "page",
    path: "/todo/:slug",
    element: FixTodoPage,
    layout: "app",
    gate: "authenticated",
    discovery: { reason: "token-link" },
  },

  // ---------------------------------------------------
  // GGD PACK — per-requirement compliance board for an inspection
  // (docs/plans/2026-09-07-ggd-pack.md). 'manager' because data entry is
  // done by Antonella AND every location manager (owner decision
  // 2026-09-07b); RLS on public.ggd_requirements is the real boundary.
  // Location-login accounts (room iPads) never reach either GGD route: the
  // location allowlist blocks them, and if the viewer-context query errors
  // they fall through to this 'manager' gate, which needs a staff_master
  // role they do not have. The roster loader also degrades for an
  // RLS-clipped groups read (src/modules/ggd/data/resolvedRoster.ts).
  // ---------------------------------------------------
  {
    kind: "page",
    path: "/staff/ggd-pack",
    element: GgdPackPage,
    layout: "app",
    gate: "manager",
    // No sidebar entry yet: the manager rail's shape is the approved nav-ia
    // rev2 contract (RAIL_SPEC in src/routes/discoveryRail.ts, mirroring
    // docs/plans/nav-ia/target-tree.json), and adding a 14th tab to it is a
    // nav-IA decision this task was not asked to make. Managers reach the
    // board by URL until the pack ships its own entry (Task 6).
    discovery: { reason: "drill-down" },
  },

  // The same board, laid out for paper (docs/plans/2026-09-07-ggd-pack.md
  // Task 6). `layout: "app"` because every non-public route renders inside
  // the app shell regardless of this field (App.tsx nests them all under
  // Layout) — the chrome is dropped at PRINT time by ggdPack.print.css, not
  // by the route. The inputs travel in the query string so a sheet is
  // reproducible.
  {
    kind: "page",
    path: "/staff/ggd-pack/print",
    element: GgdPackPrintPage,
    layout: "app",
    gate: "manager",
    discovery: { reason: "drill-down" },
  },

  // ---------------------------------------------------
  // STAFF FILL — decision table (Phase A: data layer + skeleton).
  // No sidebar entry — reached via a link shared directly with managers.
  // Mirrors app_can_manage_billing() (the source data is invoice-run
  // comparison output).
  // ---------------------------------------------------
  {
    kind: "page",
    path: "/staff/fill/:slug",
    element: StaffFill,
    layout: "app",
    gate: "billingManager",
    discovery: { reason: "token-link" },
  },

  // ---------------------------------------------------
  // MOCK ROUTES — local and Vercel Preview builds only
  // ---------------------------------------------------
  ...MOCK_ROUTES,

  // ---------------------------------------------------
  // CATCH-ALL
  // App.tsx: `isAuthenticated ? <NotFound /> : <Navigate to="/auth" />`.
  // The unauthenticated branch is auth-shell fallback behavior (see note
  // above) and isn't modeled here; this entry represents the authenticated
  // 404 page itself. None of the five reasons is a perfect fit for a 404 —
  // 'redirect' is the closest (its sibling branch on this same route IS a
  // redirect, and a 404 is a terminal, non-nav-discoverable fallback like a
  // redirect target). Flagged for conductor review.
  // ---------------------------------------------------
  {
    kind: "page",
    path: "*",
    element: NotFound,
    layout: "none",
    gate: "authenticated",
    discovery: { reason: "redirect" },
  },
];
